<!-- src/routes/contact.svelte -->
<script>
  import { onMount } from 'svelte';
  import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
  import { app } from '../lib/firebase.js';
  import { set_meta } from '../lib/seo.js';
  import { CONTACT_EMAIL } from '../lib/config.js';

  let name = '';
  let email = '';
  let message = '';
  let consent = false;
  let honeypot = ''; // bot trap — real users never fill the hidden field
  let is_submitting = false;
  let submitted = false;
  let error_message = '';

  const email_regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  $: is_valid = email_regex.test(email) && message.trim().length >= 2 && consent;

  // Lazily create the Functions instance and connect to the emulator only once
  // (connecting twice on the same instance throws).
  let functions_instance;
  function get_functions_instance() {
    if (!functions_instance) {
      functions_instance = getFunctions(app);
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        connectFunctionsEmulator(functions_instance, 'localhost', 5001);
      }
    }
    return functions_instance;
  }

  async function handle_submit() {
    if (!is_valid) {
      error_message = 'Please add a valid email, a short message, and check the consent box.';
      return;
    }
    error_message = '';
    is_submitting = true;

    try {
      const functions = get_functions_instance();
      const submit_contact = httpsCallable(functions, 'submitContact');
      const result = await submit_contact({ name, email, message, consent, website: honeypot });

      if (result && result.data && result.data.success === true) {
        submitted = true;
        name = '';
        email = '';
        message = '';
        consent = false;
      } else {
        throw new Error('Submission failed. Please try again.');
      }
    } catch (error) {
      console.error('Contact submission error:', error);
      error_message = (error && error.message) || `Something went wrong. Please email us at ${CONTACT_EMAIL}.`;
    } finally {
      is_submitting = false;
    }
  }

  onMount(() => {
    set_meta({
      title: 'Contact — Get in Touch',
      description: 'Questions about the AI LIFT or working together? Send a message and Tom will get back to you.',
      path: '/contact'
    });
  });
</script>

<section class="contact_section">
  <div class="content_wrapper">
    <div class="header_section">
      <h1 class="marker-font">Let's talk</h1>
      <p class="intro">
        Wondering if the AI LIFT is right for your team, or just have a
        question? Send a note and Tom will get back to you personally.
      </p>
    </div>

    <div class="contact_content">
      {#if submitted}
        <div class="contact_card success_card" data-test-id="contact-success-message" role="status" aria-live="polite">
          <h2>Thanks — your message is in. 🎉</h2>
          <p>
            Tom reads these personally and will get back to you soon. In the
            meantime, you can also reach us at
            <a href="mailto:{CONTACT_EMAIL}" class="email_link">{CONTACT_EMAIL}</a>.
          </p>
        </div>
      {:else}
        <form class="contact_card contact_form" on:submit|preventDefault={handle_submit} data-test-id="contact-form" novalidate>
          <!-- Honeypot: hidden from real users, catnip for bots. Do not remove. -->
          <div class="hp_field" aria-hidden="true">
            <label for="contact-website">Website (leave blank)</label>
            <input id="contact-website" type="text" tabindex="-1" autocomplete="off" bind:value={honeypot} />
          </div>

          <div class="form_field">
            <label for="contact-name">Name</label>
            <input
              id="contact-name"
              type="text"
              bind:value={name}
              placeholder="Your name"
              autocomplete="name"
              data-test-id="contact-name-input"
            />
          </div>

          <div class="form_field">
            <label for="contact-email">Email <span class="required">*</span></label>
            <input
              id="contact-email"
              type="email"
              bind:value={email}
              placeholder="you@company.com"
              autocomplete="email"
              required
              data-test-id="contact-email-input"
            />
          </div>

          <div class="form_field">
            <label for="contact-message">How can we help? <span class="required">*</span></label>
            <textarea
              id="contact-message"
              bind:value={message}
              placeholder="A sentence or two about where your AI is stuck…"
              rows="5"
              required
              data-test-id="contact-message-input"
            ></textarea>
          </div>

          <div class="form_field checkbox_field">
            <label class="checkbox_label">
              <input
                type="checkbox"
                bind:checked={consent}
                data-test-id="contact-consent-checkbox"
              />
              <span>It's okay to email me back at this address.</span>
            </label>
          </div>

          {#if error_message}
            <div class="error_message" role="alert" data-test-id="contact-error-message">
              {error_message}
            </div>
          {/if}

          <button
            type="submit"
            class="submit_button"
            class:ready={is_valid}
            disabled={is_submitting}
            data-test-id="contact-submit-button"
          >
            {is_submitting ? 'Sending…' : 'Send message'}
          </button>

          <p class="fallback">
            Prefer email? <a href="mailto:{CONTACT_EMAIL}" class="email_link">{CONTACT_EMAIL}</a>
          </p>
        </form>
      {/if}
    </div>
  </div>
</section>

<style>
  .contact_section {
    padding: 6rem 2rem;
    background: var(--color-background);
  }

  .content_wrapper {
    width: 100%;
    margin: 0 auto;
  }

  .header_section {
    width: 100%;
    margin: 0 auto 3rem;
    text-align: center;
  }

  h1 {
    font-size: 3.5rem;
    line-height: 1.2;
    margin-bottom: 1rem;
    color: var(--color-text);
    text-shadow: 2px 2px 0 rgba(0, 0, 0, 0.2);
  }

  .intro {
    font-size: 1.3rem;
    color: var(--color-text-muted);
    max-width: 640px;
    margin: 0 auto;
    line-height: 1.6;
  }

  .contact_content {
    max-width: 640px;
    margin: 0 auto;
  }

  .contact_card {
    background: var(--color-card-bg);
    padding: 2.5rem;
    border-radius: 12px;
    border: 1px solid var(--color-card-hover);
  }

  /* Honeypot — kept in the DOM (so bots fill it) but off-screen for humans. */
  .hp_field {
    position: absolute;
    left: -9999px;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }

  .form_field {
    margin-bottom: 1.5rem;
    text-align: left;
  }

  label {
    display: block;
    color: var(--color-text);
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  .required {
    color: var(--color-secondary);
    margin-left: 0.15rem;
  }

  input[type="text"],
  input[type="email"],
  textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid var(--color-card-hover);
    border-radius: 6px;
    background: var(--color-background);
    color: var(--color-text);
    font-size: 1rem;
    font-family: inherit;
    transition: all 0.2s ease;
    box-sizing: border-box;
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }

  input:focus,
  textarea:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(127, 219, 202, 0.25);
  }

  input::placeholder,
  textarea::placeholder {
    color: var(--color-text-muted);
    opacity: 0.7;
  }

  .checkbox_field {
    margin-bottom: 1.25rem;
  }

  .checkbox_label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-weight: 400;
  }

  .checkbox_label input[type="checkbox"] {
    width: auto;
    margin: 0;
  }

  .checkbox_label span {
    font-size: 0.95rem;
    color: var(--color-text);
  }

  .submit_button {
    width: 100%;
    padding: 1rem;
    border: none;
    border-radius: 6px;
    background: var(--color-primary);
    color: var(--color-background);
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .submit_button:hover:not(:disabled) {
    background: var(--color-secondary);
    transform: translateY(-1px);
  }

  .submit_button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .error_message {
    padding: 0.75rem;
    margin-bottom: 1rem;
    border-radius: 6px;
    background: rgba(232, 141, 114, 0.12);
    color: var(--color-secondary);
    text-align: center;
  }

  .fallback {
    margin: 1.25rem 0 0;
    text-align: center;
    color: var(--color-text-muted);
    font-size: 0.95rem;
  }

  .email_link {
    color: var(--color-primary);
    text-decoration: none;
  }

  .email_link:hover {
    color: var(--color-secondary);
  }

  .success_card {
    text-align: center;
  }

  .success_card h2 {
    font-size: 1.8rem;
    color: var(--color-primary);
    margin-bottom: 1rem;
  }

  .success_card p {
    font-size: 1.1rem;
    color: var(--color-text-muted);
    line-height: 1.6;
    margin: 0;
  }

  @media (max-width: 768px) {
    .contact_section {
      padding: 4rem 1rem;
    }

    h1 {
      font-size: 2.5rem;
    }

    .intro {
      font-size: 1.1rem;
    }

    .contact_card {
      padding: 1.75rem;
    }
  }
</style>
